import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Button,
  Grid,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import StorefrontIcon from "@material-ui/icons/Storefront";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import SendIcon from "@material-ui/icons/Send";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import RefreshIcon from "@material-ui/icons/Refresh";
import { Alert } from "@material-ui/lab";
import { LoadingSpinner } from "../../../utils/LoadingSpinner";
import JsonRenderComponent from "../../../utils/JsonRenderComponent";
import { ValidationUtils } from "../utils/ValidationUtils";
import { parseMailingAddress } from "../utils/AddressParser";
import PatientDetailsForm from "../components/PatientDetailsForm";
import PlaceOfServiceSelector from "../components/PlaceOfServiceSelector";
import PrescriberSelector from "../components/PrescriberSelector";
import MedicationSelector from "../components/MedicationSelector";
import PharmacyDetails from "../components/PharmacyDetails";
import { generateMessageId } from "../utils/PrescriptionHelpers";
import {
  useUPMSIngestion,
  useCustomerCreation,
  useCustomerOptions,
  usePrescriptionForm,
  useTransferRxForm,
  useTransferRxDefaults,
} from "../hooks";
import { COLORS } from "../styles/theme";
import { PREDEFINED_ADDRESSES } from "../config/addresses";
import HealthProfileSection from "../components/HealthProfileSection";
import CcsReviewPanel from "../components/CcsReviewPanel";

interface IProps {
  data?: any;
}

const UnifiedTransferRxWorkflowSection: React.FC<IProps> = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [emailPrefix, setEmailPrefix] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isUpdatingFields, setIsUpdatingFields] = useState(false);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState<number>(0);

  const step1Ref = useRef<HTMLDivElement>(null);
  const step2Ref = useRef<HTMLDivElement>(null);

  // Use TransferRx form hook for all transfer-specific state
  const transferForm = useTransferRxForm();
  const {
    sourcePharmacyNCPDP,
    setSourcePharmacyNCPDP,
    sourcePharmacyNPI,
    setSourcePharmacyNPI,
    sourcePharmacyBusinessName,
    setSourcePharmacyBusinessName,
    sourcePharmacyAddressLine1,
    setSourcePharmacyAddressLine1,
    sourcePharmacyCity,
    setSourcePharmacyCity,
    sourcePharmacyState,
    setSourcePharmacyState,
    sourcePharmacyZipCode,
    setSourcePharmacyZipCode,
    refillsRemaining,
    setRefillsRemaining,
    quantityRemaining,
    setQuantityRemaining,
    quantityTransferred,
    setQuantityTransferred,
    expirationDate,
    setExpirationDate,
    writtenDate,
    setWrittenDate,
    lastFillDate,
    setLastFillDate,
    buildTransferRxData,
  } = transferForm;

  const {
    isIngesting,
    upmsResponse,
    error: upmsError,
    ingestTransferRx,
  } = useUPMSIngestion();
  const {
    isEnrolling,
    enrollmentResponse,
    error: customerError,
    createCustomer,
    getCustomerAddresses,
    getAddressIdForZipCode,
  } = useCustomerCreation();
  const {
    options,
    handlePrimeChange,
    handleRxPassSubscriptionChange,
    handleRxPassForceActiveChange,
    handleHornbillSubscriptionChange,
    handleSetDefaultAddressChange,
    handleCommonAllergiesChange,
    handleCommonHealthConditionsChange,
    handlePregnantOrNursingChange,
    handleGenderChange,
  } = useCustomerOptions();
  const {
    patientDetails,
    prescriberDetails,
    medicationDetails,
    selectedPrescriber,
    selectedMedication,
    placeOfService,
    fieldErrors,
    setPatientField,
    updatePatientDetails,
    handlePrescriberChange,
    handleMedicationChange,
    handleMedicationFieldChange,
    handlePlaceOfServiceChange,
    updateFieldErrors,
  } = usePrescriptionForm();

  const syncMedicationWrittenDate = useCallback(
    (date: string) => handleMedicationFieldChange("writtenDate", date),
    [handleMedicationFieldChange]
  );
  useTransferRxDefaults(transferForm, medicationDetails, syncMedicationWrittenDate);

  // Scroll to active step when it changes
  useEffect(() => {
    const scrollToStep = () => {
      const ref = activeStep === 0 ? step1Ref : step2Ref;

      setTimeout(() => {
        ref.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    };

    scrollToStep();
  }, [activeStep]);

  const handleCreateCustomer = async () => {
    try {
      // Get address ID for the selected address
      let residentialAddressId: string | undefined;
      if (
        selectedAddressIndex >= 0 &&
        selectedAddressIndex < PREDEFINED_ADDRESSES.length
      ) {
        const selectedAddress = PREDEFINED_ADDRESSES[selectedAddressIndex];
        // Get address ID for the selected zipCode
        const addressId = await getAddressIdForZipCode(selectedAddress.zipCode);
        residentialAddressId = addressId || undefined;
      }

      const response = await createCustomer({
        emailPrefix: emailPrefix.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        residentialAddressId,
        options,
      });

      // Show loading indicator while fetching and updating fields
      setIsUpdatingFields(true);

      const personId = response.profile?.[0]?.personId;
      if (personId && response.profile && response.profile[0]) {
        const addresses = await getCustomerAddresses(personId);
        const profile = response.profile[0];
        const demographicProfile = profile.demographicProfile;
        if (demographicProfile) {
          const mailingAddress = addresses[0]?.mailingAddress || "";
          const parsedAddress = parseMailingAddress(mailingAddress);

          const genderCode =
            demographicProfile.gender === "Male"
              ? "M"
              : demographicProfile.gender === "Female"
              ? "F"
              : demographicProfile.gender || "M";

          updatePatientDetails({
            firstName: demographicProfile.legalFirstName || "",
            lastName: demographicProfile.legalLastName || "",
            dateOfBirth: demographicProfile.dateOfBirth || "",
            gender: genderCode,
            phoneNumber: profile.phoneNumber || response.phoneNumber || "",
            addressLine1: parsedAddress.addressLine1,
            addressLine2: parsedAddress.addressLine2,
            city: parsedAddress.city,
            state: parsedAddress.state,
            zipCode: parsedAddress.zipCode,
          });

          // Hide loading indicator after fields are updated
          setIsUpdatingFields(false);
        } else {
          setIsUpdatingFields(false);
        }
      } else {
        setIsUpdatingFields(false);
      }
    } catch (err) {
      // Error is already handled by the hook
      setIsUpdatingFields(false);
    }
  };

  const handleIngestToUPMS = async () => {
    const patientValidation = ValidationUtils.validatePatientDetails(patientDetails);
    const medicationValidation = ValidationUtils.validateMedicationDetails({
      daysSupply: medicationDetails.daysSupply,
      ndc: medicationDetails.ndc,
      sigText: medicationDetails.sigText,
      quantity: medicationDetails.quantity,
      writtenDate: medicationDetails.writtenDate,
    });

    // Combine field errors from both validations
    const combinedFieldErrors = {
      ...patientValidation.fieldErrors,
      ...medicationValidation.fieldErrors,
    };

    updateFieldErrors(combinedFieldErrors);

    if (!patientValidation.isValid || !medicationValidation.isValid) {
      return;
    }
    try {
      const transferRxData = buildTransferRxData(
        patientDetails,
        prescriberDetails,
        medicationDetails,
        placeOfService
      );
      await ingestTransferRx(
        transferRxData,
        `transfer_${generateMessageId()}.json`
      );
    } catch (err) {
      // Error is already handled by the hook
    }
  };

  const handleReset = () => {
    // Reload the page to reset all state including hook states
    window.location.reload();
  };

  const transferHandoff = {
    personId: enrollmentResponse?.profile?.[0]?.personId,
    drugDescription: medicationDetails.drugDescription,
    ndc: medicationDetails.ndc,
  };

  const renderAccordion = (title: string, data: any) => (
    <Grid item xs={12}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>{title}</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper
            style={{
              padding: 16,
              width: "100%",
              maxHeight: 400,
              overflow: "auto",
              backgroundColor: "#f5f5f5",
            }}
          >
            <JsonRenderComponent jsonObject={data} printHtml={false} />
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );

  const renderStep1 = () => {
    const hasCustomer = !!enrollmentResponse;
    const hasUPMSResponse = !!upmsResponse;

    return (
      <div ref={step1Ref}>
        <Grid container spacing={3}>
          {!hasCustomer ? (
            <>
              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#e3f2fd" }}>
                  <Typography variant="h6" gutterBottom>
                    Step 1a: Create Pharmacy Customer
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Auto-generate a new pharmacy customer with random data
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email Prefix (Optional)"
                      variant="outlined"
                      value={emailPrefix}
                      onChange={(e) => setEmailPrefix(e.target.value)}
                      placeholder="e.g., john.doe"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone Number (Optional)"
                      variant="outlined"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="e.g., 1234567890"
                      helperText="Leave empty to auto-generate"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel>Select Address</InputLabel>
                  <Select
                    value={selectedAddressIndex}
                    onChange={(e) =>
                      setSelectedAddressIndex(e.target.value as number)
                    }
                    label="Select Address"
                  >
                    {PREDEFINED_ADDRESSES.map((address: any, index: number) => (
                      <MenuItem key={index} value={index}>
                        {address.addressLine1}
                        {address.addressLine2 && `, ${address.addressLine2}`}
                        {`, ${address.city}, ${address.stateName}, ${address.zipCode}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 4, display: "block" }}
                >
                  Select a predefined address for the customer
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Options
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.prime}
                          onChange={(e) => handlePrimeChange(e.target.checked)}
                          color="primary"
                        />
                      }
                      label="Prime Member"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassSubscription}
                          onChange={(e) =>
                            handleRxPassSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="RxPass Subscription (requires Prime)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.rxPassForceActive}
                          onChange={(e) =>
                            handleRxPassForceActiveChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="RxPass Force Active (requires Prime + RxPass)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.hornbillSubscription}
                          onChange={(e) =>
                            handleHornbillSubscriptionChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Hornbill Subscription"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={options.setDefaultAddress}
                          onChange={(e) =>
                            handleSetDefaultAddressChange(e.target.checked)
                          }
                          color="primary"
                        />
                      }
                      label="Set Default Address"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <HealthProfileSection
                gender={options.gender}
                commonAllergies={options.commonAllergies}
                commonHealthConditions={options.commonHealthConditions}
                pregnantOrNursing={options.pregnantOrNursing}
                onGenderChange={handleGenderChange}
                onCommonAllergiesChange={handleCommonAllergiesChange}
                onCommonHealthConditionsChange={handleCommonHealthConditionsChange}
                onPregnantOrNursingChange={handlePregnantOrNursingChange}
              />
              {customerError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>Customer Creation Error:</strong> {customerError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleCreateCustomer}
                  disabled={isEnrolling || !!enrollmentResponse}
                  startIcon={<PersonAddIcon />}
                  style={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 8,
                    padding: "12px 32px",
                    fontSize: 16,
                    backgroundColor: COLORS.orange,
                    color: "#000",
                    boxShadow: `0 3px 10px ${COLORS.orange}40`,
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!isEnrolling) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow = `0 6px 16px ${COLORS.orange}50`;
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = `0 3px 10px ${COLORS.orange}40`;
                  }}
                >
                  {isEnrolling ? "Creating Customer..." : "Create Customer"}
                </Button>
              </Grid>
            </>
          ) : !hasUPMSResponse ? (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  Customer created! PersonId:{" "}
                  {enrollmentResponse?.profile?.[0]?.personId}
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Paper style={{ padding: 24, backgroundColor: "#fff3e0" }}>
                  <Typography variant="h6" gutterBottom>
                    Step 1b: Configure & Ingest TransferRx
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Patient details are auto-filled. Configure transfer details
                    and submit to UPMS.
                  </Typography>
                </Paper>
              </Grid>

              {isUpdatingFields && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    ⏳ Updating patient details fields with customer
                    information...
                  </Alert>
                </Grid>
              )}
              <PatientDetailsForm
                patientDetails={patientDetails}
                onFieldChange={setPatientField}
                fieldErrors={fieldErrors}
              />
              <PlaceOfServiceSelector
                placeOfService={placeOfService}
                onPlaceOfServiceChange={handlePlaceOfServiceChange}
              />
              <PrescriberSelector
                selectedPrescriber={selectedPrescriber}
                prescriberDetails={prescriberDetails}
                onPrescriberChange={handlePrescriberChange}
              />
              <MedicationSelector
                selectedMedication={selectedMedication}
                medicationDetails={medicationDetails}
                onMedicationChange={handleMedicationChange}
                onMedicationFieldChange={handleMedicationFieldChange}
                fieldErrors={fieldErrors}
              />
              <PharmacyDetails />
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <StorefrontIcon style={{ color: "#546e7a" }} />
                  Source Pharmacy (Transferring From)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="NCPDP ID"
                  variant="outlined"
                  value={sourcePharmacyNCPDP}
                  onChange={(e) => setSourcePharmacyNCPDP(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="NPI"
                  variant="outlined"
                  value={sourcePharmacyNPI}
                  onChange={(e) => setSourcePharmacyNPI(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Business Name"
                  variant="outlined"
                  value={sourcePharmacyBusinessName}
                  onChange={(e) =>
                    setSourcePharmacyBusinessName(e.target.value)
                  }
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Address Line 1"
                  variant="outlined"
                  value={sourcePharmacyAddressLine1}
                  onChange={(e) =>
                    setSourcePharmacyAddressLine1(e.target.value)
                  }
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="City"
                  variant="outlined"
                  value={sourcePharmacyCity}
                  onChange={(e) => setSourcePharmacyCity(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="State"
                  variant="outlined"
                  value={sourcePharmacyState}
                  onChange={(e) => setSourcePharmacyState(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Zip Code"
                  variant="outlined"
                  value={sourcePharmacyZipCode}
                  onChange={(e) => setSourcePharmacyZipCode(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  gutterBottom
                  style={{
                    marginTop: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <SwapHorizIcon style={{ color: "#546e7a" }} />
                  Transfer Details
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Refills Remaining"
                  variant="outlined"
                  type="number"
                  value={refillsRemaining}
                  onChange={(e) => setRefillsRemaining(e.target.value)}
                  fullWidth
                  required
                  helperText="Must be less than medication's total refills"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity Remaining"
                  variant="outlined"
                  type="number"
                  value={quantityRemaining}
                  onChange={(e) => setQuantityRemaining(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Quantity Transferred"
                  variant="outlined"
                  type="number"
                  value={quantityTransferred}
                  onChange={(e) => setQuantityTransferred(e.target.value)}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Fill Date"
                  variant="outlined"
                  type="date"
                  value={lastFillDate}
                  onChange={(e) => setLastFillDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Written Date"
                  variant="outlined"
                  type="date"
                  value={writtenDate}
                  onChange={(e) => setWrittenDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expiration Date"
                  variant="outlined"
                  type="date"
                  value={expirationDate}
                  onChange={(e) => setExpirationDate(e.target.value)}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {isIngesting && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    ⏳ Ingesting TransferRx to UPMS...
                  </Alert>
                </Grid>
              )}

              {upmsError && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <strong>UPMS Ingestion Error:</strong> {upmsError}
                  </Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleIngestToUPMS}
                  disabled={isIngesting || !!upmsResponse}
                  startIcon={<SendIcon />}
                  style={{
                    backgroundColor: COLORS.orange,
                    color: "#000",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  {isIngesting ? "Ingesting to UPMS..." : "Ingest to UPMS"}
                </Button>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={12}>
                <Alert severity="success">
                  Customer created! PersonId:{" "}
                  {enrollmentResponse?.profile?.[0]?.personId}
                </Alert>
              </Grid>

              {renderAccordion(
                "Customer Creation Response",
                enrollmentResponse
              )}
              <Grid item xs={12}>
                <Alert severity="success">
                  TransferRx successfully ingested to UPMS!
                </Alert>
              </Grid>
              {renderAccordion("UPMS Ingestion Response", upmsResponse)}
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(1)}
                  startIcon={<ArrowForwardIcon />}
                  style={{
                    backgroundColor: COLORS.orange,
                    color: "#000",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Complete Workflow
                </Button>
              </Grid>
            </>
          )}
        </Grid>
        {(isEnrolling || isIngesting) && <LoadingSpinner />}
      </div>
    );
  };

  const renderStep2Complete = () => (
    <div ref={step2Ref}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            style={{
              padding: 32,
              backgroundColor: "#e8f5e9",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" gutterBottom style={{ color: "#2e7d32" }}>
              ✓ Workflow Complete!
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Customer created and TransferRx ingested to UPMS successfully.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
        </Grid>
        {renderAccordion("Step 1a: Customer Creation", enrollmentResponse)}
        {renderAccordion("Step 1b: UPMS Ingestion", upmsResponse)}
        <CcsReviewPanel
          mode="followUp"
          title="Route to Follow-Up (optional)"
          routedTitle="Routed to Follow-Up"
          readyHint="Please continue with this step after 2 mins once the TransferRx is ingested (the prescription needs to reach RphCheck in CCS)."
          handoff={transferHandoff}
        />
        <Grid item xs={12}>
          <Button
            variant="contained"
            onClick={handleReset}
            startIcon={<RefreshIcon />}
            style={{
              backgroundColor: COLORS.orange,
              color: "#000",
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Start New Workflow
          </Button>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <Grid container direction="column" justifyContent="center" spacing={3}>
      <Grid item xs={12} style={{ width: "100%" }}>
        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel
              icon={
                enrollmentResponse && upmsResponse ? (
                  <CheckCircleIcon style={{ color: "#4caf50" }} />
                ) : undefined
              }
            >
              Create Customer & Ingest TransferRx
            </StepLabel>
            <StepContent>{renderStep1()}</StepContent>
          </Step>
          <Step>
            <StepLabel>Workflow Complete</StepLabel>
            <StepContent>{renderStep2Complete()}</StepContent>
          </Step>
        </Stepper>
      </Grid>
    </Grid>
  );
};

export default UnifiedTransferRxWorkflowSection;
